import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import PolizzaWizard, { EMPTY_WIZARD } from '../../components/PolizzaWizard';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { Polizza, Sede, StatoPolizza } from '../../types';

const STATO_BADGE: Record<StatoPolizza, string> = {
  VERDE:   'bop-badge bop-badge--success',
  GIALLO:  'bop-badge bop-badge--warning',
  ARANCIO: 'bop-badge bop-badge--warning',
  ROSSO:   'bop-badge bop-badge--danger',
  SCADUTA: 'bop-badge bop-badge--neutral',
};

export default function PolizzePage() {
  const navigate = useNavigate();
  const [polizze,   setPolizze]   = useState<Polizza[]>([]);
  const [sediAll,   setSediAll]   = useState<Sede[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [showWizard, setShowWizard] = useState(false);

  const {
    societaId: filterSoc,
    setSocietaId: setFilterSoc,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const loadPolizze = async (socId?: string) => {
    const qs = socId ? `?societaId=${socId}` : '';
    const d = await apiFetch<{ polizze: Polizza[] }>(`/api/assicurazioni/polizze${qs}`);
    setPolizze(d.polizze);
  };

  useEffect(() => {
    if (!pronta) return;
    Promise.all([
      apiFetch<{ sedi: Sede[] }>('/api/sedi'),
      apiFetch<{ polizze: Polizza[] }>(`/api/assicurazioni/polizze${filterSoc ? `?societaId=${filterSoc}` : ''}`),
    ])
      .then(([sediRes, polRes]) => {
        setSediAll(sediRes.sedi);
        setPolizze(polRes.polizze);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [pronta, filterSoc]); // eslint-disable-line

  const handleSaved = () => {
    setShowWizard(false);
    setLoading(true);
    loadPolizze(filterSoc || undefined).finally(() => setLoading(false));
  };

  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const columns = useMemo<BopExcelColumn<Polizza>[]>(() => [
    {
      key: 'numero',
      header: 'Numero',
      accessor: (p) => p.numero,
      filter: { type: 'text' },
      width: '160px',
      render: (p) => <strong>{p.numero}</strong>,
    },
    {
      key: 'compagnia',
      header: 'Compagnia',
      accessor: (p) => p.compagnia,
      filter: { type: 'text' },
      render: (p) => p.compagnia,
    },
    {
      key: 'societa',
      header: 'Società',
      accessor: (p) => p.societa_nome || '',
      filter: { type: 'text' },
      cellClassName: 'bop-text-muted',
      render: (p) => p.societa_nome,
    },
    {
      key: 'sedi',
      header: 'Sedi coperte',
      accessor: (p) => p.sedi?.map(s => s.nome).join(' ') || '',
      filter: { type: 'text' },
      cellClassName: 'bop-polizze__sedi-cell',
      render: (p) => p.sedi && p.sedi.length > 0
        ? p.sedi.map(s => s.nome).join(', ')
        : <em className="bop-polizze__empty-dash">—</em>,
    },
    {
      key: 'dataInizio',
      header: 'Inizio',
      accessor: (p) => p.dataInizio || '',
      filter: { type: 'dateRange' },
      width: '110px',
      cellClassName: 'bop-polizze__date-cell',
      render: (p) => fmtDate(p.dataInizio),
    },
    {
      key: 'dataFine',
      header: 'Scadenza',
      accessor: (p) => p.dataFine || '',
      filter: { type: 'dateRange' },
      width: '110px',
      cellClassName: 'bop-polizze__date-cell',
      render: (p) => fmtDate(p.dataFine),
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (p) => p.stato || '',
      filter: {
        type: 'enum',
        options: [
          { value: 'VERDE',   label: 'Verde' },
          { value: 'GIALLO',  label: 'Giallo' },
          { value: 'ARANCIO', label: 'Arancio' },
          { value: 'ROSSO',   label: 'Rosso' },
          { value: 'SCADUTA', label: 'Scaduta' },
        ],
      },
      width: '110px',
      render: (p) => p.stato
        ? <span className={STATO_BADGE[p.stato]}>{p.stato}</span>
        : null,
    },
  ], []);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Polizze assicurative</h2>
          <p className="bop-page-subtitle">Gestione delle polizze con scadenze e rischi coperti</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={() => setShowWizard(true)}>
          + Nuova polizza
        </button>
      </div>

      {error && <div className="bop-alert bop-alert--danger">{error}</div>}

      {/* Filtro */}
      {!isUnica && (
        <div className="bop-polizze__filter-bar">
          <div className="bop-polizze__filter-row">
            <label className="bop-polizze__filter-label">
              Società:
            </label>
            <select className="bop-select bop-polizze__filter-select"
              value={filterSoc} onChange={(e) => setFilterSoc(e.target.value)}>
              <option value="">— Tutte —</option>
              {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        </div>
      )}

      <BopExcelTable<Polizza>
        data={polizze}
        loading={loading}
        rowKey={(p) => p.id}
        onRowClick={(p) => navigate(`/assicurazioni/polizze/${p.id}`)}
        initialSort={{ key: 'dataFine', dir: 'asc' }}
        emptyState={<div className="bop-empty-state">Nessuna polizza trovata.</div>}
        columns={columns}
      />

      {showWizard && (
        <PolizzaWizard
          mode="create"
          initial={{ ...EMPTY_WIZARD }}
          societa={societa}
          sediAll={sediAll}
          onSaved={handleSaved}
          onClose={() => setShowWizard(false)}
        />
      )}
    </Layout>
  );
}
